import { FiMic } from 'react-icons/fi';

export const extendingAnswersData = {
  courseId: 'extending-answers',
  courseType: 'speaking-course',
  title: 'Extending Answers',
  icon: FiMic,
  overview: {
    impact: 'Turn short, basic answers into well-developed responses that score higher',
    focus: 'Five extension techniques: reasons, examples, comparisons, feelings, and details',
    uses: 'Speaking Parts 1, 2, and 3',
    duration: '25 minutes',
  },
  sections: [
    {
      title: 'Why Extend?',
      content: `<p>Short answers are one of the biggest reasons candidates score Band 5 or below in Fluency and Coherence. The examiner can only assess what you actually say—so the more (relevant) language you produce, the more evidence they have to give you a higher score.</p>`,
      points: [
        '<strong>Band 5:</strong> "I like reading." — Short, no development.',
        '<strong>Band 6:</strong> "I like reading because it helps me relax." — Answer + Reason.',
        '<strong>Band 7:</strong> "I like reading, especially novels, because it helps me relax after work. Last month I finished a thriller by Harlan Coben, and I couldn\'t put it down." — Answer + Reason + Example + Detail.',
        'Extending shows range of vocabulary, grammatical structures, and coherent thinking.',
        'It also fills speaking time naturally, reducing awkward pauses.',
      ],
      warning: 'Extending ≠ rambling. Every sentence should add meaningful information. If you\'re repeating yourself, stop.',
    },
    {
      title: 'Five Extension Techniques',
      content: `<p>These five techniques work for any question type. Master them and you'll never be stuck with a one-sentence answer again.</p>`,
      table: {
        headers: ['Technique', 'Signal Phrases', 'Example Extension'],
        rows: [
          ['Give a reason', '"because...", "since...", "the reason is..."', '"I enjoy cooking because it\'s creative and relaxing."'],
          ['Add an example', '"for instance...", "for example...", "like..."', '"...for example, I made Thai curry last weekend."'],
          ['Make a comparison', '"compared to...", "unlike...", "whereas..."', '"...unlike my brother, who prefers eating out."'],
          ['Express a feeling', '"I find it...", "it makes me feel...", "what I love is..."', '"...what I love most is the sense of achievement."'],
          ['Add specific detail', '"especially...", "particularly...", "in fact..."', '"...especially on weekends when I have more time."'],
        ],
      },
      points: [
        'You don\'t need all five in one answer. Two or three techniques per answer is ideal.',
        'Mix techniques to sound natural—using "because" in every answer gets repetitive.',
        'The order doesn\'t matter. Let the conversation flow naturally.',
      ],
      examples: [
        {
          label: 'Q: Do you like your neighbourhood?',
          text: '"Yes, I do. (Answer) It\'s really quiet and green, which I appreciate. (Feeling) Compared to the city centre, there\'s much less traffic. (Comparison) In fact, there\'s a lovely park just five minutes from my house where I go jogging. (Detail)"',
        },
        {
          label: 'Q: What do you usually do on weekends?',
          text: '"I usually meet up with friends. (Answer) We like going to cafés or watching films. (Example) It\'s a nice way to unwind because the week can be quite stressful. (Reason) Particularly on Saturday afternoons—that\'s when we all have time. (Detail)"',
        },
      ],
    },
    {
      title: 'Matching Techniques to Questions',
      content: `<p>Some extension techniques work better with certain question types. Here's a guide:</p>`,
      table: {
        headers: ['Question Type', 'Best Techniques', 'Example Question'],
        rows: [
          ['Opinion ("Do you like...?")', 'Reason + Example', '"Do you like travelling?"'],
          ['Description ("What is... like?")', 'Detail + Comparison', '"What is your hometown like?"'],
          ['Frequency ("How often...?")', 'Detail + Reason', '"How often do you exercise?"'],
          ['Preference ("Do you prefer...?")', 'Reason + Comparison + Feeling', '"Do you prefer mornings or evenings?"'],
          ['Change ("Has... changed?")', 'Comparison + Example + Reason', '"Has your city changed in recent years?"'],
        ],
      },
      points: [
        'For <strong>opinion questions</strong>, always give a reason. "I like it because..." is the minimum.',
        'For <strong>description questions</strong>, paint a picture with details: "It\'s a coastal city with about 200,000 people."',
        'For <strong>preference questions</strong>, compare the two options briefly before stating your choice.',
        'Don\'t force a technique that doesn\'t fit. If a comparison feels unnatural, skip it.',
      ],
    },
    {
      title: 'Avoiding Over-Extension',
      content: `<p>More isn't always better. Knowing when to stop is just as important as knowing how to extend.</p>`,
      points: [
        '<strong>Part 1:</strong> Aim for 2-4 sentences (15-25 seconds). Don\'t talk for a full minute on "Do you like chocolate?"',
        '<strong>Part 3:</strong> You can go longer (3-5 sentences), since questions are more complex and abstract.',
        '<strong>Signs you\'re over-extending:</strong> You\'re repeating yourself, going off-topic, or the examiner is trying to interrupt.',
        '<strong>Natural endings:</strong> Wrap up with a short concluding phrase: "So yeah, that\'s mainly why I enjoy it."',
      ],
      warning: 'The examiner will move on if you talk too long. It\'s better to give a concise, well-structured answer than a long, rambling one.',
      examples: [
        {
          label: 'Too long (Part 1)',
          text: '"I like coffee. I drink it every day. I usually have it in the morning. I like black coffee. Sometimes I have milk. My favourite brand is Nescafé. I also like iced coffee in summer. My friend likes tea though..." — This repeats the same idea and drifts off-topic.',
        },
        {
          label: 'Just right (Part 1)',
          text: '"I\'m a big coffee fan—I have it every morning. I prefer it black because I like the strong flavour, especially a good espresso." — Concise, with reason and detail.',
        },
      ],
    },
  ],
  exercises: [
    {
      title: 'Choose the Better Extended Answer',
      type: 'select',
      instructions: 'For each question, choose the answer that extends more effectively.',
      questions: [
        {
          text: 'Q: "Do you enjoy travelling?" — Which answer is better?',
          options: [
            '"Yes, I love travelling. I find it really exciting to experience different cultures. For example, last year I visited Japan and the food was incredible."',
            '"Yes, I do."',
            '"Yes. Travel. I like it. Travel is good. Many places. Yes."',
          ],
          answer: '"Yes, I love travelling. I find it really exciting to experience different cultures. For example, last year I visited Japan and the food was incredible."',
          explanation: 'This uses Feeling ("I find it exciting") + Example ("last year I visited Japan") + Detail ("the food was incredible"). The second is too short; the third is fragmented.',
        },
        {
          text: 'Q: "What kind of food do you prefer?" — Which answer is better?',
          options: [
            '"I\'m a big fan of Italian food, especially pasta. I find the flavours comforting, and it reminds me of meals my grandmother used to cook."',
            '"Italian food."',
            '"Food is necessary for human survival and I believe all cuisines have merit..."',
          ],
          answer: '"I\'m a big fan of Italian food, especially pasta. I find the flavours comforting, and it reminds me of meals my grandmother used to cook."',
          explanation: 'Answer + Detail + Feeling + personal connection. Specific and natural. The second is too short; the third avoids the personal question.',
        },
        {
          text: 'Q: "Do you prefer studying alone or with others?" — Which answer is better?',
          options: [
            '"Alone. I don\'t like people."',
            '"I generally prefer studying alone because I concentrate better without distractions. Unlike my classmates, who study in groups at the library, I find I\'m more productive at home."',
            '"I prefer studying alone. Alone is better. I study alone."',
          ],
          answer: '"I generally prefer studying alone because I concentrate better without distractions. Unlike my classmates, who study in groups at the library, I find I\'m more productive at home."',
          explanation: 'Uses Reason ("because I concentrate better") + Comparison ("unlike my classmates") + Detail ("more productive at home").',
        },
        {
          text: 'Q: "Has your city changed much in recent years?" — Which answer is better?',
          options: [
            '"Yes, it has changed a lot. For instance, there are many new shopping centres now compared to ten years ago. I think the city is much more modern, although I miss some of the old neighbourhoods."',
            '"Yes."',
            '"Change is inevitable in urban environments across the developing world..."',
          ],
          answer: '"Yes, it has changed a lot. For instance, there are many new shopping centres now compared to ten years ago. I think the city is much more modern, although I miss some of the old neighbourhoods."',
          explanation: 'Example + Comparison + Feeling. It gives specific evidence and a personal perspective—exactly what the examiner is looking for.',
        },
      ],
    },
    {
      title: 'Add an Extension Technique',
      type: 'fill-in',
      instructions: 'Complete each answer by adding an extension using the technique specified in brackets.',
      questions: [
        {
          text: '"I enjoy playing football. [Add a REASON] ___."',
          answer: 'It keeps me fit and I love the team spirit',
          explanation: 'A reason answers "why?" Any genuine reason works: "because it\'s fun", "since it helps me relax", etc.',
        },
        {
          text: '"I like living in my city. [Add an EXAMPLE] For instance, ___."',
          answer: 'there are great parks and a really vibrant food scene',
          explanation: 'An example makes your answer concrete and believable. Mention specific places, features, or experiences.',
        },
        {
          text: '"I prefer reading physical books. [Add a COMPARISON] Unlike ___."',
          answer: 'e-books, I enjoy the feeling of turning real pages',
          explanation: 'A comparison contrasts your preference with the alternative, showing you can discuss both sides.',
        },
        {
          text: '"I go to the gym three times a week. [Add a DETAIL] Especially ___."',
          answer: 'on Mondays, Wednesdays, and Fridays after work',
          explanation: 'Specific details (when, where, how) add depth without adding unnecessary length.',
        },
      ],
    },
    {
      title: 'Identify the Extension Technique Used',
      type: 'select',
      instructions: 'Which extension technique is the speaker using in the underlined part?',
      questions: [
        {
          text: '"I enjoy hiking *because it gives me a chance to disconnect from technology*."',
          options: ['Reason', 'Example', 'Comparison', 'Feeling'],
          answer: 'Reason',
          explanation: '"Because" signals a reason—explaining WHY they enjoy hiking.',
        },
        {
          text: '"I prefer trains to buses. *Unlike buses, trains are more comfortable and you can walk around*."',
          options: ['Comparison', 'Reason', 'Detail', 'Example'],
          answer: 'Comparison',
          explanation: '"Unlike" signals a comparison between two things—trains and buses.',
        },
        {
          text: '"I love my neighbourhood. *For instance, there\'s a beautiful lake just ten minutes away*."',
          options: ['Example', 'Comparison', 'Feeling', 'Reason'],
          answer: 'Example',
          explanation: '"For instance" introduces a specific example that supports the main point.',
        },
        {
          text: '"I\'ve been learning guitar for two years. *What I love most is the sense of progress when I master a new song*."',
          options: ['Feeling', 'Reason', 'Detail', 'Comparison'],
          answer: 'Feeling',
          explanation: '"What I love most is..." expresses a personal feeling or emotional response.',
        },
      ],
    },
    {
      title: 'Extend a Short Answer',
      type: 'writing',
      instructions: 'Take the short answer and extend it to 3-4 sentences using at least two different techniques.',
      questions: [
        {
          text: 'Short answer: "I like watching films."',
          example: 'I really enjoy watching films, especially thrillers and sci-fi. I find them exciting because you never know what\'s going to happen next. For instance, I recently watched Inception, and the plot twists kept me on the edge of my seat.',
          tip: 'Use Detail ("especially thrillers") + Reason ("because...") + Example ("for instance, I recently...").',
        },
        {
          text: 'Short answer: "I prefer summer."',
          example: 'I\'d say I prefer summer because I can spend more time outdoors. Unlike winter, when it gets dark early and cold, summer gives me the energy to go running or have barbecues with friends. It just puts me in a better mood overall.',
          tip: 'Use Reason ("because...") + Comparison ("unlike winter...") + Feeling ("it puts me in a better mood").',
        },
        {
          text: 'Short answer: "I take the bus to work."',
          example: 'I usually take the bus to work since it\'s the most affordable option. The journey takes about 30 minutes, which I actually enjoy because I use the time to listen to podcasts. Compared to driving, it\'s much less stressful.',
          tip: 'Use Reason ("since...") + Detail ("30 minutes... listen to podcasts") + Comparison ("compared to driving").',
        },
      ],
    },
    {
      title: 'Match Question to Best Technique',
      type: 'select',
      instructions: 'Which extension technique is MOST useful for each question type?',
      questions: [
        {
          text: '"Do you like your job?" — This is an opinion question. Which technique is most important?',
          options: [
            'Give a reason (explain why you do or don\'t)',
            'Make a comparison',
            'Add specific detail (time, place)',
          ],
          answer: 'Give a reason (explain why you do or don\'t)',
          explanation: 'Opinion questions need reasons above all. "I like it because..." directly answers what the examiner wants to hear.',
        },
        {
          text: '"What is your hometown like?" — This is a description question. Which technique is most important?',
          options: [
            'Add specific detail (size, features, atmosphere)',
            'Express a feeling',
            'Give a reason',
          ],
          answer: 'Add specific detail (size, features, atmosphere)',
          explanation: 'Description questions need concrete details to paint a picture: "It\'s a coastal city with about 500,000 people."',
        },
        {
          text: '"Do you prefer mornings or evenings?" — This is a preference question. Which technique is most useful after giving a reason?',
          options: [
            'Make a comparison (contrast both options)',
            'Add an example of a specific morning/evening',
            'Express a feeling without comparing',
          ],
          answer: 'Make a comparison (contrast both options)',
          explanation: 'Preference questions naturally invite comparison: "I prefer mornings because I\'m more productive. Unlike evenings, when I tend to feel tired..."',
        },
      ],
    },
  ],
  tips: [
    'Extend every answer with at least one technique — "because", "for example", or "especially"',
    'Mix your techniques. Don\'t start every sentence with "because"',
    'Listen to the question type and choose the best-fit technique',
    'Stop when you\'ve made your point. 3-4 sentences is usually enough for Part 1',
    'Practice the signal phrases ("for instance", "unlike", "what I enjoy is") until they feel automatic',
  ],
  dailyPractice: [
    'Take any simple statement ("I like pizza") and extend it using all five techniques',
    'Answer 5 Part 1 questions aloud, using at least 2 extension techniques per answer',
    'Record yourself and check: did you give a reason? An example? Was it the right length?',
    'Practice with the Liwaza AI Speaking partner to get feedback on your extensions',
  ],
  backPath: '/exams/english/#speaking',
  backLabel: 'Back to Speaking',
  aiContext: 'Extending Answers: Five techniques to develop short answers (reasons, examples, comparisons, feelings, details). Matching techniques to question types (opinion, description, preference, frequency, change). Avoiding over-extension. Difficulty 3.',
};

export function getDataForDifficulty(difficulty = 3) {
  if (difficulty <= 2) {
    return {
      ...extendingAnswersData,
      title: extendingAnswersData.title + ' (Foundations)',
      overview: { ...extendingAnswersData.overview, duration: '15-20 minutes' },
      sections: extendingAnswersData.sections.slice(0, Math.max(2, Math.ceil(extendingAnswersData.sections.length / 2))),
      exercises: extendingAnswersData.exercises.slice(0, 1).map(ex => ({
        ...ex,
        title: ex.title + ' (Basic)',
        questions: ex.questions ? ex.questions.slice(0, Math.min(3, ex.questions.length)) : [],
      })),
    };
  }
  if (difficulty >= 4) {
    return {
      ...extendingAnswersData,
      title: extendingAnswersData.title + ' (Advanced)',
      overview: { ...extendingAnswersData.overview, duration: '30-35 minutes' },
      sections: extendingAnswersData.sections,
      exercises: extendingAnswersData.exercises.map(ex => ({
        ...ex,
        title: ex.title + ' (Advanced)',
      })),
      tips: [
        ...(extendingAnswersData.tips || []),
        'For Band 8+: Focus on natural-sounding delivery rather than just accuracy.',
        'Advanced tip: Integrate this skill naturally in timed IELTS speaking practice.',
      ],
    };
  }
  return extendingAnswersData;
}
